import React, {createContext, useState} from 'react';
import {auth} from '../config/firebaseConfig';
import {createUserWithEmailAndPassword, signInWithEmailAndPassword, deleteUser, reload, sendEmailVerification} from "firebase/auth";
import {useMutation} from '@apollo/client';
import {ADD_CLUB} from "../graphql/clubModel/ClubMutation";
import getFriendlyFirebaseError from "../utils/getFriendlyFirebaseError";
import cache from '../config/apolloCache';
import { clearApolloCacheForUser } from '../config/apolloCachePersistence';
import { requiresVenueEmailVerification } from '../config/authPolicy';
import logger, { createRequestId } from '../utils/logger';

export const AuthContext = createContext({});

let activeAuthFlows = 0;
const authFlowWaiters = new Set();
const beginAuthFlow = () => { activeAuthFlows += 1; };
const endAuthFlow = () => {
    activeAuthFlows = Math.max(0, activeAuthFlows - 1);
    if (activeAuthFlows === 0) {
        authFlowWaiters.forEach(resolve => resolve());
        authFlowWaiters.clear();
    }
};
export const waitForAuthFlow = () => activeAuthFlows === 0
    ? Promise.resolve()
    : new Promise(resolve => authFlowWaiters.add(resolve));

export const AuthProvider = ({children}) => {
    const [club, setClub] = useState(null);
    const [clubDetails, setClubDetails] = useState(null);
    const [addClub] = useMutation(ADD_CLUB);
    const pendingRegistrationKey = 'boozeon:pending-venue-registration';
    const verificationSentKey = email => `boozeon:verification-sent:${email.toLowerCase()}`;
    const verificationResendCooldownMs = 10 * 60 * 1000;
    const rememberVerificationSent = email => window.localStorage.setItem(
        verificationSentKey(email), String(Date.now())
    );
    const verificationWasSentRecently = email => {
        const sentAt = Number(window.localStorage.getItem(verificationSentKey(email)));
        return Number.isFinite(sentAt) && Date.now() - sentAt < verificationResendCooldownMs;
    };

    const clubVariables = registeringClub => ({
        name: registeringClub.clubName,
        address: registeringClub.address,
        city: registeringClub.city,
        state: registeringClub.state,
        country: registeringClub.country,
        zipCode: registeringClub.zipCode,
        localityArea: registeringClub.localityArea,
        email: registeringClub.email,
        contactPersonName: registeringClub.contactPersonName,
        contactPersonNumber: registeringClub.contactPersonNumber
    });

    const completePendingRegistration = async firebaseUser => {
        if (!firebaseUser?.emailVerified) return false;
        const serialized = window.sessionStorage.getItem(pendingRegistrationKey);
        if (!serialized) return false;
        const variables = JSON.parse(serialized);
        if (variables.email?.toLowerCase() !== firebaseUser.email?.toLowerCase()) return false;
        await firebaseUser.getIdToken(true);
        const response = await addClub({ variables });
        window.sessionStorage.removeItem(pendingRegistrationKey);
        setClubDetails(response.data.addClub);
        return true;
    };

    return (<AuthContext.Provider
        value={{
            club,
            setClub,
            clubDetails,
            setClubDetails,
            login: async (email, password) => {
                const flowId = createRequestId();
                const startedAt = Date.now();
                beginAuthFlow();
                logger.info('auth.login_started', { flowId });
                let credential;
                try {
                    credential = await signInWithEmailAndPassword(auth, email, password);
                    if (requiresVenueEmailVerification) await reload(credential.user);
                    if (requiresVenueEmailVerification && !credential.user.emailVerified) {
                        const sentAgain = !verificationWasSentRecently(credential.user.email);
                        if (sentAgain) {
                            await sendEmailVerification(credential.user);
                            rememberVerificationSent(credential.user.email);
                        }
                        await auth.signOut();
                        logger.warn('auth.login_blocked_unverified_email', { flowId, verificationResent: sentAgain });
                        alert(sentAgain
                            ? `A verification email was sent to ${credential.user.email}. Use only this latest email.`
                            : `Your email is not verified yet. Use the latest verification email sent to ${credential.user.email}; another link was not generated.`
                        );
                        return false;
                    }
                    await completePendingRegistration(credential.user);
                    logger.info('auth.login_completed', { flowId, durationMs: Date.now() - startedAt });
                    return true;
                } catch (error) {
                    if (credential?.user) await auth.signOut().catch(() => {});
                    const message = getFriendlyFirebaseError(error);
                    logger.error('auth.login_failed', { flowId, durationMs: Date.now() - startedAt, error });
                    alert(message);
                    return false;
                } finally {
                    endAuthFlow();
                }
            },
            register: async (registeringClub) => {
                const flowId = createRequestId();
                const startedAt = Date.now();
                beginAuthFlow();
                logger.info('auth.registration_started', { flowId });
                try {
                    const credential = await createUserWithEmailAndPassword(auth, registeringClub.email, registeringClub.password);
                    const variables = clubVariables(registeringClub);
                    if (requiresVenueEmailVerification) {
                        window.sessionStorage.setItem(pendingRegistrationKey, JSON.stringify(variables));
                        await sendEmailVerification(credential.user);
                        rememberVerificationSent(credential.user.email);
                        await auth.signOut();
                        alert(`Verification email sent to ${credential.user.email}. Check spam or promotions, then verify and sign in to finish venue registration.`);
                    } else {
                        await credential.user.getIdToken(true);
                        const response = await addClub({ variables });
                        setClubDetails(response.data.addClub);
                    }
                    logger.info('auth.registration_completed', {
                        flowId,
                        durationMs: Date.now() - startedAt,
                        verificationRequired: requiresVenueEmailVerification,
                    });
                    return true;
                } catch (error) {
                    window.sessionStorage.removeItem(pendingRegistrationKey);
                    let firebaseUser = auth.currentUser;
                    if (firebaseUser) {
                        try {
                            await auth.signOut();
                            await deleteUser(firebaseUser);
                            logger.warn('auth.registration_rollback_completed', { flowId });
                        } catch (rollbackErr) {
                            logger.error('auth.registration_rollback_failed', { flowId, error: rollbackErr });
                        }
                    }
                    const message = getFriendlyFirebaseError(error);
                    logger.error('auth.registration_failed', { flowId, durationMs: Date.now() - startedAt, error });
                    alert(message);
                    return false;
                } finally {
                    endAuthFlow();
                }
            },
            logout: async () => {
                const flowId = createRequestId();
                logger.info('auth.logout_started', { flowId });
                try {
                    const userId = auth.currentUser?.uid;
                    await clearApolloCacheForUser(cache, userId);
                    await auth.signOut();
                    setClub(null);
                    setClubDetails(null);
                    logger.info('auth.logout_completed', { flowId });
                    // Ensure URL resets to '/' after logout and avoid landing on /Home
                    window.location.replace('/');
                } catch (e) {
                    logger.error('auth.logout_failed', { flowId, error: e });
                }
            },
        }}>
        {children}
    </AuthContext.Provider>);
};
